import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  removeWorkerSession,
  updateWorkerSession,
  subscribeToWorkerSession,
} from '../utils/firebase';
import { WorkerInfo } from '../types/auth';
import { toast } from 'react-hot-toast';

interface AuthState {
  token: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  workerId: string | null;
  workerName: string | null;
  workerInfo: WorkerInfo | null;
  deviceId: string | null;
  tenantId: number | null;
  isAuthenticated: boolean;
  setAuth: (
    token: string,
    accessToken: string,
    refreshToken: string,
    workerId: string,
    workerName: string,
    workerInfo: WorkerInfo,
    deviceId: string,
    tenantId: number
  ) => void;
  logout: () => Promise<void>;
}

const initialState = {
  token: null,
  accessToken: null,
  refreshToken: null,
  workerId: null,
  workerName: null,
  workerInfo: null,
  deviceId: null,
  tenantId: null,
  isAuthenticated: false,
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      ...initialState,
      setAuth: (
        token: any,
        accessToken: any,
        refreshToken: any,
        workerId: string,
        workerName: any,
        workerInfo: any,
        deviceId: any,
        tenantId: any
      ) => {
        if (!token || !workerId) {
          console.error('Invalid auth data:', {
            token,
            workerId,
            workerName,
            workerInfo,
          });
          return;
        }
        set({
          token,
          accessToken,
          refreshToken,
          workerId,
          workerName,
          workerInfo,
          deviceId,
          tenantId,
          isAuthenticated: true,
        });

        // Subscribe to session changes
        const unsubscribe = subscribeToWorkerSession(
          workerId,
          async (session) => {
            if (session?.force_logout || !session?.is_logged_in) {
              // Force logout if session is marked for logout or no longer active
              const currentState = get();
              if (currentState.isAuthenticated) {
                toast.error('You have been logged out from another device', {
                  duration: 5000,
                });
                await get().logout();
                // Only reload after logout is complete
                window.location.href = '/login';
              }
            }
          }
        );

        // Start a heartbeat to update last_active
        const heartbeatInterval = setInterval(async () => {
          const currentState = get();
          if (currentState.workerId && currentState.isAuthenticated) {
            try {
              await updateWorkerSession(currentState.workerId, {
                last_active: Date.now(),
                is_logged_in: true,
                device_id: currentState.deviceId,
              });
            } catch (error) {
              if (
                error instanceof Error &&
                error.message === 'Session has been logged out'
              ) {
                clearInterval(heartbeatInterval);
                unsubscribe();
                toast.error('You have been logged out from another device', {
                  duration: 5000,
                });
                await get().logout();
                // Only reload after logout is complete
                window.location.href = '/login';
              } else {
                console.error('Failed to update heartbeat:', error);
              }
            }
          } else {
            clearInterval(heartbeatInterval);
            unsubscribe();
          }
        }, 10000); // Update every 10 seconds

        // Return cleanup function
        return () => {
          clearInterval(heartbeatInterval);
          unsubscribe();
        };
      },
      logout: async () => {
        const { workerId } = get();
        if (workerId) {
          try {
            await removeWorkerSession(workerId);
            localStorage.clear();
            set(initialState);
          } catch (error) {
            console.error('Logout error:', error);
            // Still clear local state even if Firebase cleanup fails
            localStorage.clear();
            set(initialState);
          }
        } else {
          localStorage.clear();
          set(initialState);
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      version: 1,
      partialize: (state) => ({
        token: state.token,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        workerId: state.workerId,
        workerName: state.workerName,
        workerInfo: state.workerInfo,
        deviceId: state.deviceId,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.token && state?.workerId) {
          state.isAuthenticated = true;
        }
      },
    }
  )
);
